type NodeProperty =
  | 'parentNode'
  | 'previousSibling'
  | 'nextSibling'
  | 'firstChild';

export const contentReferenceClass = 'content-ref';
export const externalReferenceClass = 'external-ref';

/**
 * Manually accessing and manipulating the HTML DOM tree while using React can cause issues.
 * Sometimes, fiber nodes from the React shadow tree are accessed mistakenly. This function helps identify and ignore them.
 *
 * @param {Node} node - The `Node` object to test if it is a fiber node.
 * @returns {boolean} Indicates whether the given node is a fiber node.
 */
export const isFiberNode = (node: Node) =>
  node?.nodeType === Node.TEXT_NODE &&
  node.textContent &&
  (node.textContent.match(/^\n$/g) || node.textContent.match(/^$/g)) &&
  node.textContent?.length === 1;

/**
 * Identifies text nodes.
 *
 * @param node - The `Node` object to test if it is a text node.
 * @returns {boolean} A boolean indicating whether the given node is a text node.
 */
export const isTextNode = (node: Node) =>
  node?.nodeType === Node.TEXT_NODE && !isFiberNode(node);

/**
 * Identifies span elements that are placeholders for imported nodes.
 *
 * @param {Node} node - The `Node` object to test if it is a span element.
 * @returns {boolean} Indicates whether the given node is a span element.
 */
const isImportedLinkSpan = (node: Node): boolean => {
  const el = node as Element;
  return (
    node.nodeName === 'SPAN' &&
    (el.classList.contains(contentReferenceClass) ||
      el.classList.contains(externalReferenceClass))
  );
};

/**
 * Identifies button and icon button elements.
 *
 * @param {Node} node - The `Node` object to test if it is a button or icon button element.
 * @returns {boolean} Indicates whether the given node is a button or icon button element.
 */
export const isAccessibleButton = (node: Node): boolean =>
  node.nodeName === 'BUTTON' ||
  node.nodeName === 'I' ||
  (node as Element).role === 'button';

/**
 * Identifies whether a node related to dynamically added elements (links, marks, and search word highlights) should be ignored when calculating the position of these elements.
 *
 * @param {Node} node - The `Node` object to test if it should be ignored.
 * @returns {boolean} Indicates whether the given node should be ignored.
 */
export const getIgnoreNode = (node: Node): boolean =>
  node.nodeName === 'MARK' ||
  node.nodeName === 'A' ||
  isImportedLinkSpan(node) ||
  isAccessibleButton(node);

/**
 * Finds the closest parent, nextSibling, previousSibling, or childNode that is not a fiber node ({@link isFiberNode}).
 *
 * @param {Node} node - The node representing the starting point from which the parent, nextSibling, previousSibling, or childNode is calculated.
 * @param {NodeProperty} nodeProperty - Defines the relationship of the desired node to the given node.
 * @returns {Node | null} - The desired node or `null` if no such node exists.
 */
export const getNodeIgnoreFiberNodes = (
  node: Node,
  nodeProperty: NodeProperty,
): Node | null => {
  const n = node[`${nodeProperty}`] as Node;

  if (isFiberNode(n)) {
    return getNodeIgnoreFiberNodes(n, nodeProperty);
  }

  return n;
};
